import { useRef, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Download, Loader2, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShareImageButton } from "@/components/ShareCard";
import { loadImg, wrap } from "@/components/ClassStar";
import { useLang } from "@/context/LanguageContext";
import { SIMRAN, certUrl } from "@/lib/aiot";

const S = 1080;
const day = (iso) => new Date(iso || Date.now()).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
const fileName = (lab, name, ext) => `RoboSpark-AIoT-${lab.id}-lab-certificate-${(name || "Student").replace(/\s+/g, "-")}.${ext}`;
const qrFor = (no, width) => QRCode.toDataURL(certUrl(no), { margin: 1, width, color: { dark: "#0B1F3A" } });

const cover = (ctx, img, w, h) => {
  const r = Math.max(w / img.width, h / img.height);
  ctx.drawImage(img, (img.width - w / r) / 2, (img.height - h / r) / 2, w / r, h / r, 0, 0, w, h);
};

// 1080x1080 share image: the student's finished 3D room on top, name and lab below.
const drawLabCard = async ({ lab, cert, snap }) => {
  await document.fonts?.ready;
  const c = Object.assign(document.createElement("canvas"), { width: S, height: S });
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#F3F8FB"; ctx.fillRect(0, 0, S, S);
  if (snap) { try { cover(ctx, await loadImg(snap), S, 640); } catch { /* plain background */ } }
  const g = ctx.createLinearGradient(0, 400, 0, 680);
  g.addColorStop(0, "rgba(243,248,251,0)"); g.addColorStop(1, "#F3F8FB");
  ctx.fillStyle = g; ctx.fillRect(0, 400, S, 280);
  ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.roundRect(50, 50, 300, 84, 22); ctx.fill();
  ctx.fillStyle = "#FF2E63"; ctx.beginPath(); ctx.roundRect(66, 66, 52, 52, 12); ctx.fill();
  ctx.font = "800 38px Outfit, sans-serif"; ctx.textAlign = "left";
  ctx.fillStyle = "#FF2E63"; ctx.fillText("Robo", 134, 106);
  ctx.fillStyle = "#0B1F3A"; ctx.fillText("Spark", 134 + ctx.measureText("Robo").width, 106);
  ctx.save(); ctx.translate(S - 60, 60); ctx.rotate(0.05);
  ctx.fillStyle = "#0F766E"; ctx.beginPath(); ctx.roundRect(-340, 0, 340, 124, 24); ctx.fill();
  ctx.fillStyle = "#fff"; ctx.textAlign = "right"; ctx.font = "800 22px Outfit, 'Noto Sans Devanagari', sans-serif"; ctx.fillText("3D LAB CERTIFIED", -30, 44);
  ctx.font = "900 50px Outfit, sans-serif"; ctx.fillStyle = "#5EEAD4"; ctx.fillText("100/100", -30, 100);
  ctx.restore();
  const qr = await loadImg(await qrFor(cert.no, 300));
  ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.roundRect(S - 240, 230, 180, 214, 20); ctx.fill();
  ctx.drawImage(qr, S - 225, 245, 150, 150);
  ctx.fillStyle = "#0B1F3A"; ctx.textAlign = "center"; ctx.font = "700 16px Outfit, sans-serif"; ctx.fillText(cert.no, S - 150, 425);
  ctx.textAlign = "left"; ctx.fillStyle = "#0F766E"; ctx.font = "800 28px Outfit, sans-serif";
  ctx.fillText("ADVANCED AIoT DIPLOMA · PERFECT BUILD", 70, 720);
  ctx.fillStyle = "#0B1F3A"; ctx.font = "900 112px Outfit, sans-serif"; ctx.fillText((cert.name || "Student").split(" ")[0].slice(0, 14), 64, 845);
  ctx.fillStyle = "#334155"; ctx.font = "600 34px Outfit, 'Noto Sans Devanagari', sans-serif";
  wrap(ctx, `${lab.title} · ${lab.title_hi}`, S - 140, 2).forEach((line, i) => ctx.fillText(line, 70, 905 + i * 44));
  ctx.fillStyle = "#14B8A6"; ctx.fillRect(70, 995, 130, 9);
  ctx.fillStyle = "#64748B"; ctx.font = "500 24px Outfit, sans-serif"; ctx.textAlign = "right";
  ctx.fillText("Learn · Build · Earn  —  robospark", S - 70, 1006);
  return new Promise((res) => c.toBlob(res, "image/png"));
};

// A4-landscape PDF certificate.
const CertDoc = ({ lab, cert, snap, qr, innerRef }) => (
  <div ref={innerRef} style={{ width: 1123, height: 794, background: "#EAF2FB", fontFamily: "'Outfit','Noto Sans Devanagari',sans-serif", padding: 28, boxSizing: "border-box" }}>
    <div style={{ background: "#fff", border: "3px solid #14B8A6", borderRadius: 24, height: "100%", boxSizing: "border-box", padding: "36px 56px", color: "#0B1F3A", position: "relative",
      backgroundImage: "radial-gradient(520px 300px at 0% 0%, rgba(20,184,166,0.12), transparent), radial-gradient(520px 300px at 100% 100%, rgba(29,78,216,0.10), transparent)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 22, fontWeight: 800 }}>
        <span><span style={{ color: "#FF2E63" }}>Robo</span>Spark <span style={{ fontSize: 11, letterSpacing: 3, color: "#64748B" }}>PRP PVT LTD</span></span>
        <span style={{ fontSize: 13, letterSpacing: 3, color: "#0F766E" }}>ADVANCED AIoT DIPLOMA</span>
      </div>
      <div style={{ display: "flex", gap: 36, marginTop: 30 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, color: "#0F766E", fontWeight: 700 }}><span style={{ letterSpacing: 5 }}>CERTIFICATE OF ACHIEVEMENT · </span><span style={{ fontSize: 16 }}>उपलब्धि प्रमाणपत्र</span></div>
          <div style={{ marginTop: 8, fontSize: 50, fontWeight: 900, lineHeight: 1.05 }}>3D Lab Certified</div>
          <div style={{ marginTop: 6, fontSize: 30, fontWeight: 900, color: "#0F766E" }}>100 / 100</div>
          <div style={{ marginTop: 22, fontSize: 14, color: "#64748B" }}>Awarded to · प्रदान किया गया</div>
          <div style={{ marginTop: 4, fontSize: 40, fontWeight: 800, borderBottom: "3px solid #14B8A6", display: "inline-block", paddingBottom: 6 }}>{cert.name || "Student"}</div>
          <div style={{ marginTop: 18, fontSize: 16, color: "#334155", lineHeight: 1.65 }}>
            for a perfect build in the <b>{lab.title}</b>: every device placed in the right spot, wired correctly to the ESP32, and every automation scenario passed on the server.<br />
            <span style={{ fontSize: 15 }}>{lab.title_hi} में परफ़ेक्ट बिल्ड: हर डिवाइस सही जगह, सही वायरिंग और हर सीनारियो पास।</span>
          </div>
        </div>
        <div style={{ width: 400 }}>
          {snap ? <img src={snap} alt="" style={{ width: 400, height: 280, objectFit: "cover", borderRadius: 18, border: "4px solid #CCFBF1" }} />
            : <div style={{ width: 400, height: 280, borderRadius: 18, background: "#F0FDFA" }} />}
          <div style={{ marginTop: 6, fontSize: 12, color: "#0F766E", fontWeight: 700, textAlign: "center" }}>The finished build · तैयार बिल्ड</div>
        </div>
      </div>
      <div style={{ position: "absolute", left: 56, right: 56, bottom: 34, display: "flex", justifyContent: "space-between", alignItems: "flex-end", fontSize: 13 }}>
        <div style={{ color: "#334155", display: "flex", alignItems: "flex-end", gap: 14 }}>
          {qr && <img src={qr} alt="" style={{ width: 96, height: 96, border: "1px solid #CBD5E1", borderRadius: 8 }} />}
          <div>Certificate No: <b>{cert.no}</b><br />Date: <b>{day(cert.at)}</b><br /><span style={{ fontSize: 11, color: "#0F766E" }}>Scan to verify · /verify-aiot</span></div>
        </div>
        <div style={{ textAlign: "center" }}>
          <img src={SIMRAN[0]} alt="" style={{ width: 84, height: 84, borderRadius: "50%", objectFit: "cover", border: "4px solid #5EEAD4" }} />
          <div style={{ fontSize: 12, color: "#0F766E", fontWeight: 700 }}>Simran Ma'am · AI Course Instructor</div>
        </div>
        <div style={{ textAlign: "right" }}><div style={{ borderTop: "1px solid #0B1F3A", paddingTop: 4, width: 220 }}>Director, RoboSpark PrP Pvt Ltd</div><span style={{ fontSize: 11, color: "#64748B" }}>C-30, C-Block, Sector 63, Noida-201301 (U.P.)</span></div>
      </div>
    </div>
  </div>
);

export const LabCertActions = ({ lab, cert, snap, testid = "lab-cert" }) => {
  const { L } = useLang();
  const ref = useRef(null);
  const [doc, setDoc] = useState(null);
  const pdf = async () => {
    try {
      setDoc({ snap: snap?.() || null, qr: await qrFor(cert.no, 240) });
      const html2pdf = (await import("html2pdf.js")).default;
      await new Promise((r) => setTimeout(r, 40)); // certificate is mounted only while generating
      await Promise.all([...ref.current.querySelectorAll("img")].map((i) => i.decode().catch(() => {})));
      await html2pdf().set({
        margin: 0, filename: fileName(lab, cert.name, "pdf"), image: { type: "jpeg", quality: 0.95 }, html2canvas: { scale: 2 },
        jsPDF: { unit: "px", format: [1123, 794], orientation: "landscape", hotfixes: ["px_scaling"] },
      }).from(ref.current).save();
    } catch { toast.error(L("Could not create the certificate.", "प्रमाणपत्र नहीं बन सका।")); } finally { setDoc(null); }
  };
  const first = (cert.name || "").split(" ")[0];
  const text = L(`${first} scored a perfect 100/100 in RoboSpark's ${lab.title} (Advanced AIoT Diploma)! Verify: ${certUrl(cert.no)}`,
    `${first} ने RoboSpark की ${lab.title_hi} (एडवांस्ड AIoT डिप्लोमा) में 100/100 स्कोर किया! जाँचें: ${certUrl(cert.no)}`);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      <Button onClick={pdf} disabled={!!doc} className="rounded-full bg-[#0F766E] hover:bg-[#0B1F3A] text-white" data-testid={`${testid}-pdf-btn`}>
        {doc ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />{L("Certificate PDF", "प्रमाणपत्र PDF")}</>}
      </Button>
      <ShareImageButton makeBlob={() => drawLabCard({ lab, cert, snap: snap?.() })} fname={fileName(lab, cert.name, "png")} text={text}
        title={L("Share your lab certificate", "अपना लैब प्रमाणपत्र शेयर करें")} testid={`${testid}-share-btn`} />
      {doc && <div aria-hidden style={{ position: "fixed", left: -14000, top: 0 }}><CertDoc lab={lab} cert={cert} snap={doc.snap} qr={doc.qr} innerRef={ref} /></div>}
    </div>
  );
};

export const LabCertCard = ({ lab, cert, snap }) => {
  const { L } = useLang();
  return (
    <div className="rounded-2xl border-2 border-[#5EEAD4] bg-[#F0FDFA] p-4" data-testid="lab-cert-card">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#0F766E] text-white grid place-items-center shrink-0"><Award className="w-5 h-5" /></span>
        <div className="min-w-0">
          <div className="text-sm font-extrabold text-[#0B1F3A]">{L("Perfect build! Your certificate is ready", "परफ़ेक्ट बिल्ड! आपका प्रमाणपत्र तैयार है")}</div>
          <div className="text-[11px] text-slate-600">{L("It includes a snapshot of your room as it looks right now, so set a nice camera angle first.", "इसमें आपके कमरे की अभी की तस्वीर आएगी, इसलिए पहले कैमरा एंगल अच्छा सेट कीजिए।")} · <span className="font-bold" data-testid="lab-cert-no">{cert.no}</span></div>
        </div>
      </div>
      <div className="mt-3"><LabCertActions lab={lab} cert={cert} snap={snap} /></div>
    </div>
  );
};
